import React, { useEffect, useState } from "react";
import { updateKol } from "../services/kolService";
import { useAppDispatch, useAppSelector } from "../hooks/useAppDispatch";
import {
  Typography,
  Table,
  Button,
  Space,
  Input,
  Modal,
  Form,
  Select,
  InputNumber,
  Popconfirm,
  Tag,
  Card,
  Row,
  Col,
  Divider,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  InstagramOutlined,
  ExpandOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import {
  fetchKols,
  addKol,
  editKol,
  removeKol,
  setSelectedKol,
  setFilters,
  clearFilters,
  Kol,
} from "../store/slices/kolSlice";
import { RootState } from "../store";
import type { ColumnsType } from "antd/es/table";

const { Title } = Typography;
const { Option } = Select;

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const KolDetailCard = styled(Card)`
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
`;

// 示例的分类选项（实际项目中可从 API 获取）
const categoryOptions = [
  "Beauty",
  "Fashion",
  "Lifestyle",
  "Fitness",
  "Technology",
  "Food",
  "Travel",
  "Entertainment",
  "Gaming",
  "Business",
];

const KolManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const { kols, selectedKol, isLoading, filters } = useAppSelector(
    (state: RootState) => state.kol
  );

  // 控制模态框、详情模态框、编辑状态及提交错误
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(fetchKols());
  }, [dispatch]);

  useEffect(() => {
    if (selectedKol && isEdit) {
      form.setFieldsValue({
        ...selectedKol,
        // 将后台的 bio 映射到表单的 categories 字段
        categories: Array.isArray(selectedKol.bio)
          ? selectedKol.bio.join(", ")
          : selectedKol.bio,
      });
    }
  }, [selectedKol, form, isEdit]);

  // 打开添加/编辑模态框
  const handleShowModal = (edit = false, record?: Kol) => {
    setIsEdit(edit);
    if (edit && record) {
      dispatch(setSelectedKol(record));
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  // 关闭模态框
  const handleCancel = () => {
    setIsModalVisible(false);
    setIsEdit(false);
    dispatch(setSelectedKol(null));
    form.resetFields();
  };

  // 表单提交函数
  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitError(null);
      // 按照之前的逻辑处理 categories 字段
      let categories = values.categories;
      if (typeof categories === "string") {
        categories = categories.split(",").map(cat => cat.trim()).filter(cat => cat !== "");
      }
      values.bio = categories;
      delete values.categories;
      if (isEdit && selectedKol) {
        const updatedKol = await updateKol(selectedKol.id, values);
        dispatch(editKol({ id: selectedKol.id, data: updatedKol }));
      } else {
        dispatch(addKol(values));
      }
      setIsModalVisible(false);
      setIsEdit(false);
      form.resetFields();
    } catch (error) {
      console.error("Validation failed or update error:", error);
      
      if (error instanceof Error) {
        setSubmitError(error.message);
      } else {
        setSubmitError("Failed to submit form. Please check your inputs.");
      }
    }
  };


  const handleDelete = (id: string) => {
    dispatch(removeKol(id));
  };

  const handleViewDetails = (record: Kol) => {
    dispatch(setSelectedKol(record));
    setIsDetailModalVisible(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setFilters({ search: e.target.value }));
  };

  const handleCategoryChange = (value: string) => {
    dispatch(setFilters({ category: value }));
  };

  const handleFollowerFilterChange = (
    field: "minInstagramFollowers" | "minTiktokFollowers",
    value: number | null
  ) => {
    dispatch(setFilters({ [field]: value || 0 }));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  const filteredKols = kols.filter((kol: Kol) => {
    const matchesSearch = filters.search
      ? (kol.name ?? "").toLowerCase().includes(filters.search.toLowerCase()) ||
        (kol.email ?? "").toLowerCase().includes(filters.search.toLowerCase()) ||
        (kol.instagramHandle ?? "")
          .toLowerCase()
          .includes(filters.search.toLowerCase()) ||
        (kol.tiktokHandle ?? "")
          .toLowerCase()
          .includes(filters.search.toLowerCase())
      : true;

    const matchesCategory = filters.category
    ? kol.bio && kol.bio.includes(filters.category)
    : true;


    const matchesInstagramFollowers =
      kol.instagramFollowers >= filters.minInstagramFollowers;
    const matchesTiktokFollowers =
      kol.tiktokFollowers >= filters.minTiktokFollowers;

    return (
      matchesSearch &&
      matchesCategory &&
      matchesInstagramFollowers &&
      matchesTiktokFollowers
    );
  });

  const columns: ColumnsType<Kol> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Instagram",
      dataIndex: "instagramHandle",
      key: "instagramHandle",
      render: (text) => (
        <Space>
          <InstagramOutlined style={{ color: "#E1306C" }} />
          {text}
        </Space>
      ),
    },
    {
      title: "Instagram Followers",
      dataIndex: "instagramFollowers",
      key: "instagramFollowers",
      sorter: (a, b) => a.instagramFollowers - b.instagramFollowers,
      render: (followers) => new Intl.NumberFormat().format(followers),
    },
    {
      title: "TikTok Followers",
      dataIndex: "tiktokFollowers",
      key: "tiktokFollowers",
      sorter: (a, b) => a.tiktokFollowers - b.tiktokFollowers,
      render: (followers) => new Intl.NumberFormat().format(followers),
    },
    {
      title: "Categories",
      dataIndex: "bio", // 后台字段名为 bio
      key: "bio",
      render: (bio: string[] | string | undefined) => {
        if (!bio) {
          return null;
        }
        const categories = Array.isArray(bio)
          ? bio
          : bio.split(",").map((item) => item.trim());
        return (
          <>
            {categories.map((category) => (
              <Tag color="blue" key={category}>
                {category}
              </Tag>
            ))}
          </>
        );
      },
    },

    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<ExpandOutlined />}
            onClick={() => handleViewDetails(record)}
            title="View Details"
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => handleShowModal(true, record)}
            title="Edit"
          />
          <Popconfirm
            title="Are you sure you want to delete this KOL?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} title="Delete" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={2}>KOL Management</Title>

      <ActionBar>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleShowModal(false)}
        >
          Add New KOL
        </Button>
      </ActionBar>

      <FilterContainer>
        <Input
          placeholder="Search KOLs"
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 250 }}
          value={filters.search}
          onChange={handleSearchChange}
        />

        {/* <InputNumber
          placeholder="Min TikTok Followers"
          style={{ width: 200 }}
          min={0}
          value={filters.minTiktokFollowers || undefined}
          onChange={(value) =>
            handleFollowerFilterChange("minTiktokFollowers", value)
          }
        /> */}

        <Button
          icon={<DeleteOutlined />}
          type="primary"
          ghost
          onClick={handleClearFilters}
          style={{ borderRadius: 20 }}
        >
          Clear Filters
        </Button>

      </FilterContainer>

      <Table
        columns={columns}
        dataSource={filteredKols}
        rowKey="id"
        loading={isLoading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: "max-content" }}
      />

      {/* Add/Edit KOL Modal */}
      <Modal
        title={isEdit ? "Edit KOL" : "Add New KOL"}
        open={isModalVisible}
        onCancel={handleCancel}
        onOk={handleFormSubmit}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          name="kolForm"
          initialValues={{
            instagramFollowers: 0,
            tiktokFollowers: 0,
            categories: "",
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Please enter name" }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: "Please enter email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="instagramHandle"
                label="Instagram Link"
                rules={[
                  { required: true, message: "Please enter Instagram link" },
                ]}
              >
                <Input placeholder="https://instagram.com/username" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="instagramFollowers"
                label="Instagram Followers"
                rules={[
                  {
                    required: true,
                    message: "Please enter Instagram followers",
                  },
                ]}
              >
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="tiktokHandle" label="TikTok Link">
                <Input placeholder="https://tiktok.com/@username" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="tiktokFollowers" label="TikTok Followers">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          {/* 在表单中使用名称 categories，用户输入后会转换为 bio 存入数据库 */}
          <Form.Item
            name="categories"
            label="Categories"
            rules={[
              { required: true, message: "Please enter at least one category" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const regex = /^[A-Za-z\s,]+$/;
                  if (regex.test(value)) {
                    return Promise.resolve();
                  } else {
                    return Promise.reject(
                      "Only letters, spaces, and commas are allowed."
                    );
                  }
                },
              },
            ]}
          >
            <Input placeholder="Enter categories separated by commas" />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
        {submitError && (
          <div style={{ color: "red", marginTop: "10px" }}>{submitError}</div>
        )}
      </Modal>

      {/* KOL Details Modal */}
      <Modal
        title="KOL Details"
        open={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsDetailModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="edit"
            type="primary"
            onClick={() => {
              setIsDetailModalVisible(false);
              if (selectedKol) {
                handleShowModal(true, selectedKol);
              }
            }}
          >
            Edit
          </Button>,
        ]}
        width={700}
      >
        {selectedKol && (
          <KolDetailCard>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Title level={4}>{selectedKol.name}</Title>
                <p>
                  {selectedKol.email} | {selectedKol.phone}
                </p>
                <p>Location: {selectedKol.location}</p>
              </Col>

              <Divider />

              <Col span={12}>
                <Title level={5}>Social Media</Title>
                <p>
                  <Space>
                    <InstagramOutlined style={{ color: "#E1306C" }} />
                    {selectedKol.instagramHandle} (
                    {new Intl.NumberFormat().format(
                      selectedKol.instagramFollowers
                    )}{" "}
                    followers)
                  </Space>
                </p>
                {selectedKol.tiktokHandle && (
                  <p>
                    TikTok: @{selectedKol.tiktokHandle} (
                    {new Intl.NumberFormat().format(
                      selectedKol.tiktokFollowers
                    )}{" "}
                    followers)
                  </p>
                )}
              </Col>

              <Col span={12}>
                <Title level={5}>Categories</Title>
                <div>
                  {
                    selectedKol && selectedKol.bio
                      ? (Array.isArray(selectedKol.bio)
                          ? selectedKol.bio
                          : selectedKol.bio.split(",").map((item: string) => item.trim())
                        ).map((category: string) => (
                          <Tag color="blue" key={category}>
                            {category}
                          </Tag>
                        ))
                      : null // 如果 selectedKol 或 selectedKol.bio 为 null，则返回 null
                  }

                </div>
              </Col>


              {selectedKol.notes && (
                <Col span={24}>
                  <Title level={5}>Notes</Title>
                  <p>{selectedKol.notes}</p>
                </Col>
              )}
            </Row>
          </KolDetailCard>
        )}
      </Modal>
    </div>
  );
};

export default KolManagement;
